import {
  listAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  CourseAssignment,
  CourseAssignmentPayload,
} from '@training24/course/src/assignment'

interface CourseAssignmentState {
  all: CourseAssignment[]
  allLoaded: boolean
  byCourse: Record<string, CourseAssignment[]>
  loadedForCourse: Record<string, boolean>
}

export type { CourseAssignment, CourseAssignmentPayload }

export default {
  namespaced: true,

  state: (): CourseAssignmentState => ({
    all: [],
    allLoaded: false,
    byCourse: {},
    loadedForCourse: {},
  }),

  getters: {
    // All assignments for a specific course
    getAssignmentsByCourse: (state: CourseAssignmentState) => (courseId: string): CourseAssignment[] => {
      if (state.allLoaded) return state.all.filter((a) => a.courseId === courseId)
      return state.byCourse[courseId] || []
    },

    // All assignments for a specific target (group or user)
    getAssignmentsByTarget: (state: CourseAssignmentState) => (targetType: string, targetId: string): CourseAssignment[] => {
      return state.all.filter((a) => a.targetType === targetType && a.targetId === targetId)
    },

    countByCourse: (state: CourseAssignmentState) => (courseId: string): number => {
      if (state.allLoaded) return state.all.filter((a) => a.courseId === courseId).length
      return (state.byCourse[courseId] || []).length
    },

    isAllLoaded: (state: CourseAssignmentState): boolean => state.allLoaded,
  },

  actions: {
    // Load ALL assignments for the org (used by groups/users tabs)
    async LOAD_ALL(context: any, orgId: string) {
      if (context.state.allLoaded) return
      const jwt = context.rootGetters['getJWT']
      const items = await listAssignments(orgId, jwt)
      context.commit('SET_ALL', items)
    },

    // Load assignments for a specific course (used by catalog tab)
    async LOAD_FOR_COURSE(context: any, { orgId, courseId }: { orgId: string; courseId: string }) {
      // If all are already loaded, no need to load per-course
      if (context.state.allLoaded) return
      if (context.state.loadedForCourse[courseId]) return
      const jwt = context.rootGetters['getJWT']
      const items = await listAssignments(orgId, jwt, courseId)
      context.commit('SET_FOR_COURSE', { courseId, items })
    },

    async CREATE(context: any, { orgId, payload }: { orgId: string; payload: CourseAssignmentPayload }) {
      const jwt = context.rootGetters['getJWT']
      const item = await createAssignment(orgId, payload, jwt)
      context.commit('ADD', item)
      return item
    },

    async UPDATE(context: any, { orgId, assignmentId, payload }: { orgId: string; assignmentId: string; payload: Partial<CourseAssignmentPayload> }) {
      const jwt = context.rootGetters['getJWT']
      const item = await updateAssignment(orgId, assignmentId, payload, jwt)
      context.commit('REPLACE', item)
      return item
    },

    async DELETE(context: any, { orgId, assignmentId }: { orgId: string; assignmentId: string }) {
      const jwt = context.rootGetters['getJWT']
      await deleteAssignment(orgId, assignmentId, jwt)
      context.commit('REMOVE', assignmentId)
    },
  },

  mutations: {
    SET_ALL(state: CourseAssignmentState, items: CourseAssignment[]) {
      state.all = items
      state.allLoaded = true
      // Also populate byCourse cache
      const byCourse: Record<string, CourseAssignment[]> = {}
      const loadedForCourse: Record<string, boolean> = {}
      items.forEach((a) => {
        if (!byCourse[a.courseId]) byCourse[a.courseId] = []
        byCourse[a.courseId].push(a)
        loadedForCourse[a.courseId] = true
      })
      state.byCourse = byCourse
      state.loadedForCourse = loadedForCourse
    },

    SET_FOR_COURSE(state: CourseAssignmentState, { courseId, items }: { courseId: string; items: CourseAssignment[] }) {
      state.byCourse = { ...state.byCourse, [courseId]: items }
      state.loadedForCourse = { ...state.loadedForCourse, [courseId]: true }
    },

    ADD(state: CourseAssignmentState, assignment: CourseAssignment) {
      state.all = [...state.all, assignment]
      const existing = state.byCourse[assignment.courseId] || []
      state.byCourse = { ...state.byCourse, [assignment.courseId]: [...existing, assignment] }
    },

    REPLACE(state: CourseAssignmentState, assignment: CourseAssignment) {
      state.all = state.all.map((a) => (a.assignmentId === assignment.assignmentId ? assignment : a))
      const existing = state.byCourse[assignment.courseId] || []
      state.byCourse = {
        ...state.byCourse,
        [assignment.courseId]: existing.map((a) =>
          a.assignmentId === assignment.assignmentId ? assignment : a
        ),
      }
    },

    REMOVE(state: CourseAssignmentState, assignmentId: string) {
      const found = state.all.find((a) => a.assignmentId === assignmentId)
      state.all = state.all.filter((a) => a.assignmentId !== assignmentId)
      if (found) {
        const existing = state.byCourse[found.courseId] || []
        state.byCourse = {
          ...state.byCourse,
          [found.courseId]: existing.filter((a) => a.assignmentId !== assignmentId),
        }
      }
    },
  },
}
